import { memo, useEffect, useRef } from 'react';
import type { Shot } from '../types';

/**
 * Thumbnail strip of every memory in the story. Click (or Enter) to jump.
 * Uses the small generated thumbnails and lazy-loads them, so long episodes
 * stay light. The current memory is kept scrolled into view.
 */
function FilmstripImpl({
  shots,
  index,
  onJump,
  reducedMotion,
}: {
  shots: Shot[];
  index: number;
  onJump: (i: number) => void;
  reducedMotion: boolean;
}) {
  const strip = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const el = strip.current?.querySelector<HTMLElement>(`[data-shot="${index}"]`);
    el?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [index, reducedMotion]);

  return (
    <ol
      ref={strip}
      aria-label="Memories in this story"
      className="flex gap-1.5 overflow-x-auto px-1 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {shots.map((s) => {
        const src = s.item.thumbnailUrl ?? s.item.posterUrl;
        const current = s.index === index;
        return (
          <li key={s.item.id} data-shot={s.index} className="shrink-0">
            <button
              onClick={() => onJump(s.index)}
              aria-label={`Memory ${s.index + 1} of ${shots.length}${s.kind === 'video' ? ', video' : ''}`}
              aria-current={current ? 'true' : undefined}
              className={`focus-ring relative block h-10 w-16 overflow-hidden rounded-[3px] bg-[#17140f] transition sm:h-12 sm:w-[84px] ${
                current ? 'opacity-100 ring-2 ring-[#f3ece2]' : 'opacity-45 hover:opacity-85'
              }`}
            >
              {src && <img src={src} alt="" loading="lazy" draggable={false} className="h-full w-full object-cover" />}
              {s.kind === 'video' && (
                <svg viewBox="0 0 24 24" className="absolute right-1 bottom-1 h-3 w-3 text-white drop-shadow" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export const Filmstrip = memo(FilmstripImpl);
